// directive-provide-context.ts
import { ContextEvent } from "@lit/context";
import { noChange } from "lit";
import { AsyncDirective } from "lit/async-directive.js";
import { directive, PartType } from "lit/directive.js";
import { _useMemo, getCurrentComponent } from "./stateful.js";
var ValueNotifier = class {
  constructor(defaultValue) {
    this.subscriptions = /* @__PURE__ */ new Map();
    this.updateObservers = () => {
      for (const [callback, { disposer }] of this.subscriptions) {
        callback(this._value, disposer);
      }
    };
    if (defaultValue !== void 0) {
      this.value = defaultValue;
    }
  }
  get value() {
    return this._value;
  }
  set value(v) {
    this.setValue(v);
  }
  setValue(v, force = false) {
    const update = force || !Object.is(v, this._value);
    this._value = v;
    if (update) {
      this.updateObservers();
    }
  }
  addCallback(callback, consumerHost, subscribe) {
    if (!subscribe) {
      callback(this.value);
      return;
    }
    if (!this.subscriptions.has(callback)) {
      this.subscriptions.set(callback, {
        disposer: () => {
          this.subscriptions.delete(callback);
        },
        consumerHost
      });
    }
    const { disposer } = this.subscriptions.get(callback);
    callback(this.value, disposer);
  }
  clearCallbacks() {
    this.subscriptions.clear();
  }
};
var ProvideContextDirective = class extends AsyncDirective {
  constructor(partInfo) {
    super(partInfo);
    this.valueNotifier = new ValueNotifier();
    if (partInfo.type !== PartType.ELEMENT)
      throw new Error("provide-directive can only be used as ElementPart");
    if (!(partInfo.element instanceof HTMLElement))
      throw new Error("provide-directive can only be used on HTMLElement");
  }
  render(context, data) {
    return noChange;
  }
  update(part, [context, data]) {
    const newHost = part.element;
    const newContext = context;
    if (newHost !== this.host || newContext !== this.context)
      this.removeEventListener();
    this.host = newHost;
    this.context = newContext;
    this.addEventListener();
    this.valueNotifier.setValue(data);
    return noChange;
  }
  reconnected() {
    this.removeEventListener();
    this.addEventListener();
  }
  disconnected() {
    this.removeEventListener();
  }
  addEventListener() {
    const host = this.host;
    const context = this.context;
    if (!host || !context)
      return;
    if (!this.eventListenerCallback) {
      const eventListenerCallback = this.getEventListenersCallback(host, context);
      host.addEventListener("context-request", eventListenerCallback);
      this.eventListenerCallback = eventListenerCallback;
    }
  }
  removeEventListener() {
    if (this.eventListenerCallback) {
      this.host?.removeEventListener("context-request", this.eventListenerCallback);
      this.eventListenerCallback = void 0;
    }
  }
  getEventListenersCallback(host, context) {
    return (ev) => {
      if (ev.context !== context) {
        return;
      }
      const consumerHost = ev.contextTarget ?? ev.composedPath()[0];
      if (consumerHost === host) {
        return;
      }
      ev.stopPropagation();
      this.valueNotifier.addCallback(ev.callback, consumerHost, ev.subscribe);
    };
  }
};
var provideContext = directive(ProvideContextDirective);
function useContext(context) {
  return _useMemo((hook) => {
    const component = getCurrentComponent();
    const partType = component.instance.part;
    if (!partType)
      throw new Error("part doesnt exist, should not happen");
    if (partType.type !== PartType.CHILD)
      throw new Error("partType is not supported: " + partType);
    let host = partType.parentNode;
    let reqFrameReference;
    const callback = (value, unsubscribe) => {
      hook.value = value;
      hook.cleanup = () => {
        unsubscribe?.();
        cancelAnimationFrame(reqFrameReference);
      };
      component.instance.rerender();
    };
    reqFrameReference = requestAnimationFrame(() => {
      const event = new ContextEvent(context, host, callback, true);
      host.dispatchEvent(event);
    });
    return [void 0, () => cancelAnimationFrame(reqFrameReference)];
  }, []).value;
}
export {
  ProvideContextDirective,
  ValueNotifier,
  provideContext,
  useContext
};
